import axios from 'axios';
import { Movie, MovieCredits, MovieReviews } from '../types';

const TMDB_API_KEY = process.env.REACT_APP_TMDB_API_KEY;
const TMDB_BASE_URL = 'https://api.themoviedb.org/3';
const TMDB_IMAGE_BASE_URL = 'https://image.tmdb.org/t/p';
const PLACEHOLDER_IMAGE = '/images/no-image.png';

if (!TMDB_API_KEY) {
  console.error('TMDB API key is not set. Please check your environment variables.');
}

const tmdbApi = axios.create({
  baseURL: TMDB_BASE_URL,
  params: {
    api_key: TMDB_API_KEY,
  },
});

interface SearchResponse {
  results: Movie[];
  total_pages: number;
  total_results: number;
  page: number;
}

export const tmdbService = {
  getImageUrl: (path: string | null, size: 'w92' | 'w185' | 'w342' | 'w500' | 'w780' | 'original' = 'w500'): string => {
    if (!path) return PLACEHOLDER_IMAGE;
    return `${TMDB_IMAGE_BASE_URL}/${size}${path}`;
  },

  getTrendingMovies: async (page: number = 1): Promise<SearchResponse> => {
    try {
      const response = await tmdbApi.get('/trending/movie/week', {
        params: { page },
      });
      return {
        ...response.data,
        results: response.data.results.map((movie: any) => ({
          ...movie,
          poster_path: movie.poster_path || '/placeholder.jpg',
          backdrop_path: movie.backdrop_path || '/placeholder.jpg',
        })),
      };
    } catch (error) {
      console.error('Error fetching trending movies:', error);
      return {
        results: [],
        total_pages: 0,
        total_results: 0,
        page: 1,
      };
    }
  },

  getMovieDetails: async (movieId: number): Promise<Movie> => {
    try {
      const response = await tmdbApi.get(`/movie/${movieId}`);
      return {
        ...response.data,
        poster_path: response.data.poster_path || '/placeholder.jpg',
        backdrop_path: response.data.backdrop_path || '/placeholder.jpg',
      };
    } catch (error) {
      console.error('Error fetching movie details:', error);
      throw error;
    }
  },

  getMovieCredits: async (movieId: number): Promise<MovieCredits> => {
    try {
      const response = await tmdbApi.get(`/movie/${movieId}/credits`);
      return {
        ...response.data,
        cast: response.data.cast.map((actor: any) => ({
          ...actor,
          profile_path: actor.profile_path || '/placeholder.jpg',
        })),
      };
    } catch (error) {
      console.error('Error fetching movie credits:', error);
      throw error;
    }
  },

  getMovieReviews: async (movieId: number, page: number = 1): Promise<MovieReviews> => {
    try {
      const response = await tmdbApi.get(`/movie/${movieId}/reviews`, {
        params: { page },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching movie reviews:', error);
      throw error;
    }
  },

  searchMovies: async (query: string, page: number = 1): Promise<SearchResponse> => {
    try {
      const response = await tmdbApi.get('/search/movie', {
        params: {
          query,
          page,
          include_adult: false,
        },
      });
      return {
        ...response.data,
        results: response.data.results.map((movie: any) => ({
          ...movie,
          poster_path: movie.poster_path || '/placeholder.jpg',
          backdrop_path: movie.backdrop_path || '/placeholder.jpg',
        })),
      };
    } catch (error) {
      console.error('Error searching movies:', error);
      return {
        results: [],
        total_pages: 0,
        total_results: 0,
        page: 1,
      };
    }
  },
}; 